'use client';

import { useRef, useState } from 'react';

import type { LifecyclePanel } from '../../fuer-grossvermieter/_content';

type TabKey = LifecyclePanel['key'];

function StatCheck() {
  return (
    <span className="ic" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

function pillClass(variant: 'live' | 'soon' | 'idle') {
  if (variant === 'live') return 'dash-pill dash-pill--live';
  if (variant === 'soon') return 'dash-pill dash-pill--soon';
  return 'dash-pill dash-pill--idle';
}

function PanelVisual({ visual }: { visual: LifecyclePanel['visual'] }) {
  if (visual.type === 'stat') {
    return (
      <div className="statcard">
        <div className="statcard-num">{visual.num}</div>
        <p className="statcard-label">{visual.label}</p>
        <ul className="statcard-list">
          {visual.items.map((item) => (
            <li key={item}>
              <StatCheck /> {item}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="dash" role="img" aria-label={visual.ariaLabel}>
      <div className="dash-head">
        <span className="dash-title">{visual.title}</span>
        <span className="dash-meta">{visual.meta}</span>
      </div>
      {visual.rows.map((row) => (
        <div className="dash-row" key={row.unit}>
          <span className="dash-unit">
            {row.unit}
            <span>{row.detail}</span>
          </span>
          <span className={pillClass(row.pillVariant)}>{row.pill}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Lifecycle scenario tabs (for-portfolio-managers). Locale-agnostic: all text
 * arrives via `panels` from the server parent. Behaviour ported from the
 * prototype's inline JS – click + ArrowLeft/ArrowRight roving-tabindex.
 */
/**
 * 🔴 **ZWEI BAUTEILE DIESES NAMENS — und sie bleiben getrennt.** Bis zum
 * 21.09.2026 hiessen beide `LifecycleTabs`; das hat einmal schon zu einer
 * Suche in der falschen Datei geführt. Gemessen, warum sie getrennt bleiben:
 * **null gemeinsame Zeilen**, andere Datenform (`LifecyclePanel` gegen
 * `LifecycleTab`), andere Gestalt (`statcard*`/`dash*` gegen `tab-statcard*`/
 * `av-*`) und andere ARIA-Rollen (`tabpanel` gegen `region`).
 *
 * ⚠️ **Die Begründung steht in BEIDEN Dateien** — das ist die Lehre aus
 * `EmailSearchFilters`: eine Begründung, die man erst findet, nachdem man die
 * Arbeit begonnen hat, kommt zu spät. Der Name ist deshalb zusätzlich
 * unterschieden.
 *
 * Diese hier: die Grossvermieter-Seite. Schwester:
 * `so-funktionierts/_components/service/lifecycle-tabs.tsx`.
 */
export function LifecycleTabsGrossvermieter({
  panels,
  tablistLabel,
}: {
  panels: LifecyclePanel[];
  /** Lokalisiertes aria-label der Reiter-Leiste (kommt aus dem Seiten-Content). */
  tablistLabel: string;
}) {
  const order = panels.map((p) => p.key);
  const [active, setActive] = useState<TabKey>(order[0] ?? 'erst');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(event: React.KeyboardEvent, index: number) {
    const dir =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    event.preventDefault();
    const nextKey = order[(index + dir + order.length) % order.length];
    if (!nextKey) return;
    setActive(nextKey);
    tabRefs.current[nextKey]?.focus();
  }

  return (
    <div className="reveal">
      <div className="tablist-wrap">
        <div className="tablist" role="tablist" aria-label={tablistLabel}>
          {panels.map((panel, index) => {
            const on = panel.key === active;
            return (
              <button
                key={panel.key}
                ref={(el) => {
                  tabRefs.current[panel.key] = el;
                }}
                className={on ? 'tab-btn is-active' : 'tab-btn'}
                role="tab"
                aria-selected={on}
                id={`tab-${panel.key}`}
                aria-controls={`panel-${panel.key}`}
                tabIndex={on ? 0 : -1}
                onClick={() => setActive(panel.key)}
                onKeyDown={(e) => onKeyDown(e, index)}
              >
                {panel.tabLabel}
              </button>
            );
          })}
        </div>
      </div>

      {panels.map((panel) => {
        const on = panel.key === active;
        return (
          <div
            key={panel.key}
            className={on ? 'tab-panel is-active' : 'tab-panel'}
            role="tabpanel"
            id={`panel-${panel.key}`}
            aria-labelledby={`tab-${panel.key}`}
            hidden={!on}
          >
            <div className="tab-panel-body">
              <div className="tab-text">
                <h3>{panel.heading}</h3>
                <p>{panel.body}</p>
                <div className="tab-focus">
                  {panel.focusPrefix} <strong>{panel.focusStrong}</strong>
                </div>
              </div>
              <div className="tab-visual">
                <PanelVisual visual={panel.visual} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
